import React, { useState } from 'react';
import { Tooltip, ClickAwayListener } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/pro-regular-svg-icons';
import { BoxV2 as Box, Flex } from 'portal-commons';

import { toastFlashMessage } from '../../../../../utils';
import { VettingRecord } from '../../types';

const CustomTooltip = withStyles(() => ({
  arrow: {
    color: '#00797C',
  },
  tooltip: {
    background: '#FFFFFF',
    color: '#0091B3',
    fontSize: 14,
    borderRadius: '0',
    maxWidth: 526,
    width: 526,
    maxHeight: 355,
    fontWeight: 400,
    padding: 0,
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  },
}))(Tooltip);

interface Props {
  data: VettingRecord;
}

/**
 * ScoreTooltipV2 component provides a detailed tooltip for vetting records.
 *
 * This version is designed to be self-contained, with all styling applied directly
 * via `sx` props using `portal-commons` BoxV2 and Flex components.
 * It does not rely on external CSS files or ancestor class names for its appearance,
 * unlike ScoreTooltip.tsx which utilizes `brand-details-module.scss` and CSS class selectors.
 * The primary goal of ScoreTooltipV2 is to replicate the visual appearance of ScoreTooltip.tsx
 * while being independent of its surrounding CSS context.
 */
const ScoreTooltipV2: React.FC<Props> = ({ data }) => {
  const [open, setOpen] = useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  const getCopyTextTemplate = (text: string | undefined, keyName: string) => {
    if (!text) {
      return null;
    }
    return (
      <Box
        as="p"
        sx={{
          cursor: 'pointer',
          color: '#071822',
          textDecoration: 'underline',
          marginRight: '17px',
          paddingLeft: '8px',
        }}
        onClick={() => {
          navigator.clipboard.writeText(text);
          toastFlashMessage(
            `${keyName} has been copied to your clipboard`,
            'success'
          );
        }}
      >
        copy
      </Box>
    );
  };

  const remapStringReasonsToUITemplate = (stringReason: string) => {
    const array = JSON.parse(stringReason);
    return array.map((reason: string, index: number) => {
      return (
        <Box
          as="li"
          key={`reasons-${index}`}
          sx={{
            marginBottom: '4px',
            color: '#071822',
            wordBreak: 'break-word',
            listStyle: 'disc',
            display: 'list-item',
            cursor: 'text',
            '&:last-child': { marginBottom: 0 },
            marginLeft: '1em',
          }}
        >
          {reason}
        </Box>
      );
    });
  };

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <CustomTooltip
        PopperProps={{
          disablePortal: true,
          popperOptions: {
            positionFixed: true,
            modifiers: {
              preventOverflow: {
                enabled: true,
                boundariesElement: 'window',
              },
            },
          },
        }}
        open={open}
        onClose={handleTooltipClose}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        interactive
        placement={'bottom-end'}
        id="tooltip"
        title={
          <Flex
            sx={{
              flexDirection: 'column',
              maxHeight: '355px',
              cursor: 'default',
            }}
          >
            <Box
              as="h6"
              sx={{
                fontWeight: 700,
                backgroundColor: '#00698f',
                padding: '10px 20px',
                color: 'white',
                lineHeight: '24px',
                textAlign: 'left',
                fontSize: '20px',
                letterSpacing: '0em',
              }}
            >
              Please see the vetting details below:
            </Box>
            {data && data.reasons && (
              <Box
                sx={{
                  width: '100%',
                  padding: '9px 17px 0px 17px',
                }}
              >
                <Box
                  as="ul"
                  data-testid="reasons"
                  sx={{
                    padding: '20px 0',
                    maxHeight: '260px',
                    overflow: 'auto',
                  }}
                >
                  {data && data.reasons
                    ? remapStringReasonsToUITemplate(data.reasons)
                    : null}
                </Box>
              </Box>
            )}
            {data.vettingClass === 'POLITICAL' ? (
              data.evpUid === 'AEGIS' ? (
                <Box
                  sx={{
                    width: '100%',
                    overflowY: 'auto',
                    padding: '9px 17px 0px 17px',
                    flex: 1,
                  }}
                >
                  <Box
                    as="ul"
                    data-testid="politicalReasons"
                    sx={{
                      maxHeight: 'unset',
                      overflow: 'auto',
                      paddingLeft: 0,
                    }}
                  >
                    {data.additionalRequestPayload &&
                      Object.entries(
                        JSON.parse(data.additionalRequestPayload)
                      ).map(([key, value], index) => (
                        <Box
                          as="li"
                          key={`reasons-${index}`}
                          sx={{
                            marginBottom: '4px',
                            color: '#071822',
                            wordBreak: 'break-word',
                            listStyle: 'none',
                            cursor: 'text',
                            '&:last-child': { marginBottom: 0 },
                          }}
                        >
                          <span>{key}: </span> {value as React.ReactNode}
                        </Box>
                      ))}
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    overflowY: 'auto',
                    padding: '9px 17px 0px 17px',
                    flex: 1,
                  }}
                >
                  <Box
                    as="ul"
                    data-testid="politicalReasons"
                    sx={{
                      maxHeight: 'unset',
                      overflow: 'auto',
                      paddingLeft: 0,
                    }}
                  >
                    {data.fullReport &&
                      Object.entries(JSON.parse(data.fullReport)).map(
                        ([key, value], index) => (
                          <Box
                            as="li"
                            key={`reasons-${index}`}
                            sx={{
                              marginBottom: '4px',
                              color: '#071822',
                              wordBreak: 'break-word',
                              listStyle: 'none',
                              cursor: 'text',
                              '&:last-child': { marginBottom: 0 },
                            }}
                          >
                            <span>{key}: </span> {` ${value}`}
                          </Box>
                        )
                      )}
                  </Box>
                </Box>
              )
            ) : null}
            <Box sx={{ fontSize: '12px', padding: '9px 17px' }}>
              <hr />
              {data.vettingClass === 'POLITICAL' ? (
                data.evpUid === 'AEGIS' ? (
                  <Flex
                    sx={{
                      flex: 1,
                      justifyContent: 'space-between',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Box
                      as="p"
                      sx={{
                        flex: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: '#071822',
                      }}
                    >
                      <span>Vetting ID: </span> {data.vettingId || 'N/A'}
                    </Box>
                    {getCopyTextTemplate(data.vettingId, 'Vetting ID')}
                  </Flex>
                ) : (
                  <Flex sx={{ justifyContent: 'space-between' }}>
                    <Flex
                      sx={{
                        flex: 1,
                        justifyContent: 'space-between',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Box
                        as="p"
                        sx={{
                          flex: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: '#071822',
                        }}
                      >
                        <span>Vetting ID: </span> {data.vettingId || 'N/A'}
                      </Box>
                      {getCopyTextTemplate(data.vettingId, 'Vetting ID')}
                    </Flex>
                    <Flex
                      sx={{
                        flex: 1,
                        justifyContent: 'space-between',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Box
                        as="p"
                        sx={{
                          flex: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: '#071822',
                        }}
                      >
                        <span>Vetting Token: </span>{' '}
                        {data.vettingToken || 'N/A'}
                      </Box>
                      {getCopyTextTemplate(data.vettingToken, 'Vetting Token')}
                    </Flex>
                  </Flex>
                )
              ) : data.evpUid === 'WMC' ? (
                <Flex sx={{ justifyContent: 'space-between' }}>
                  <Flex
                    sx={{
                      flex: 1,
                      justifyContent: 'space-between',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Box
                      as="p"
                      sx={{
                        flex: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: '#071822',
                      }}
                    >
                      <span>Vetting ID/Transaction ID: </span>{' '}
                      {data.vettingId || 'N/A'}
                    </Box>
                    {getCopyTextTemplate(
                      data.vettingId,
                      'Vetting ID/Transaction ID'
                    )}
                  </Flex>
                  <Flex
                    sx={{
                      flex: 1,
                      justifyContent: 'space-between',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Box
                      as="p"
                      sx={{
                        flex: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: '#071822',
                      }}
                    >
                      <span>Vetting Token: </span>{' '}
                      {(data.fullReport &&
                        JSON.parse(data.fullReport).vettingToken) ||
                        'N/A'}
                    </Box>
                    {data.fullReport &&
                      getCopyTextTemplate(
                        JSON.parse(data.fullReport).vettingToken,
                        'Vetting Token'
                      )}
                  </Flex>
                </Flex>
              ) : (
                <Flex sx={{ justifyContent: 'space-between' }}>
                  <Flex
                    sx={{
                      flex: 1,
                      justifyContent: 'space-between',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Box
                      as="p"
                      sx={{
                        flex: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: '#071822',
                      }}
                    >
                      <span>Vetting ID: </span> {data.vettingId || 'N/A'}
                    </Box>
                    {getCopyTextTemplate(data.vettingId, 'Vetting ID')}
                  </Flex>
                  <Flex
                    sx={{
                      flex: 1,
                      justifyContent: 'space-between',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Box
                      as="p"
                      sx={{
                        flex: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: '#071822',
                      }}
                    >
                      <span>Vetting Token: </span> {data.vettingToken || 'N/A'}
                    </Box>
                    {getCopyTextTemplate(data.vettingToken, 'Vetting Token')}
                  </Flex>
                </Flex>
              )}
            </Box>
          </Flex>
        }
      >
        <Box
          as="span"
          onClick={handleTooltipOpen}
          data-testid="viewIcon"
          sx={{
            cursor: 'pointer',
            fontSize: '10px',
            width: '12px',
            padding: '1px 2px',
            color: '#3B5000',
            position: 'relative',
          }}
        >
          <FontAwesomeIcon icon={faEye} onClick={handleTooltipOpen} />
        </Box>
      </CustomTooltip>
    </ClickAwayListener>
  );
};

export default ScoreTooltipV2;
